import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import DeviceLinkApi from '../../../lib/api/deviceLink'
import { setLoading } from '../../../action/loading-actions'
import { getBaseURL } from '../../../lib/util'
import { normaliseCode, formatCodeForDisplay } from '../../../lib/deviceLinkCode'
import './index.scss'

/**
 * Device pairing approval (WEB-7089), the page behind the QR code a car head unit displays.
 *
 * Reached as /link?code=K7QP3MRX. The route in ../index.js renders <Login /> here instead of
 * this component when signed out, without navigating, so the code survives a sign-in and this
 * component mounts with the query string still intact.
 *
 * Mobile is the primary target, not an afterthought: the code is scanned with a phone camera.
 * That is also why 'link' is in REDIRECT_EXCLUDED_PATHS in main.js - otherwise the app store
 * redirect fires before any of this renders.
 */

// What the user is shown, and which controls they get.
const STATUS = {
  LOADING: 'loading',
  READY: 'ready',      // a real pending pairing, awaiting a decision
  APPROVED: 'approved',
  DENIED: 'denied',
  ERROR: 'error'       // unknown, expired, already used, locked, or throttled
}

const PLATFORM_LABELS = {
  AndroidAuto: 'Android Auto',
  CarPlay: 'CarPlay'
}

const formatRequestedAt = value => {
  if (!value) return null
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date.toLocaleString()
}

const DeviceLink = props => {
  const [searchParams] = useSearchParams()
  const userCode = normaliseCode(searchParams.get('code'))

  const [status, setStatus] = useState(STATUS.LOADING)
  const [pending, setPending] = useState(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { token, setLoading } = props

  // The splash screen is dismissed by each route that takes over the page; without this the
  // pairing card renders underneath it.
  useEffect(() => {
    setLoading(false)
  }, [setLoading])

  useEffect(() => {
    if (!userCode) {
      setStatus(STATUS.ERROR)
      setMessage('This link is missing its pairing code. Start again from your vehicle.')
      return
    }

    let cancelled = false

    DeviceLinkApi.getPending({ userCode, token })
      .then(res => {
        if (cancelled) return
        setPending(res)
        setStatus(STATUS.READY)
      })
      .catch(err => {
        if (cancelled) return
        setMessage(err.message)
        setStatus(STATUS.ERROR)
      })

    // The user can sign in, or be handed a new token by a refresh, while this request is in
    // flight. Without the guard a late response from the previous token overwrites the state the
    // newer one produced.
    return () => { cancelled = true }
  }, [userCode, token])

  const decide = (action, nextStatus) => {
    setSubmitting(true)
    setMessage('')

    action({ userCode, token })
      .then(() => {
        setStatus(nextStatus)
      })
      .catch(err => {
        setMessage(err.message)
        setStatus(STATUS.ERROR)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const platformLabel = pending
    ? (PLATFORM_LABELS[pending.platform] || pending.platform || 'Vehicle')
    : ''
  const requestedAt = pending ? formatRequestedAt(pending.requestedAt) : null

  return (
    <div className='device-link-bg'>
      <div className='device-link-card'>
        <h1 className='device-link-header'>Connect your vehicle</h1>

        {status === STATUS.LOADING && (
          <p className='device-link-body'>Checking that code…</p>
        )}

        {status === STATUS.READY && (
          <>
            <p className='device-link-body'>
              {`Sign in to BaseMap on ${platformLabel}?`}
            </p>

            <dl className='device-link-detail'>
              <dt>Device</dt>
              <dd>{pending.deviceName || platformLabel}</dd>
              <dt>Code</dt>
              <dd className='device-link-code'>{formatCodeForDisplay(userCode)}</dd>
              {requestedAt && (
                <>
                  <dt>Requested</dt>
                  <dd>{requestedAt}</dd>
                </>
              )}
            </dl>

            <p className='device-link-note'>
              Approving signs this vehicle in to your account. Only do this if you started the
              pairing yourself.
            </p>

            <div className='device-link-actions'>
              <button
                className='device-link-approve'
                disabled={submitting}
                onClick={() => decide(DeviceLinkApi.approve, STATUS.APPROVED)}>
                {submitting ? 'Working…' : 'Approve'}
              </button>
              <button
                className='device-link-deny'
                disabled={submitting}
                onClick={() => decide(DeviceLinkApi.deny, STATUS.DENIED)}>
                Not me
              </button>
            </div>
          </>
        )}

        {status === STATUS.APPROVED && (
          <>
            <p className='device-link-body device-link-success'>Vehicle connected.</p>
            <p className='device-link-note'>
              You can put your phone away — the sign-in finishes on the vehicle's screen.
            </p>
          </>
        )}

        {status === STATUS.DENIED && (
          <>
            <p className='device-link-body'>Pairing declined.</p>
            <p className='device-link-note'>
              Nothing was connected. If you did not start this, no further action is needed.
            </p>
          </>
        )}

        {status === STATUS.ERROR && (
          <>
            <p className='device-link-body device-link-error'>{message}</p>
            <p className='device-link-note'>
              Pairing codes expire after a few minutes. Start again from your vehicle to get a
              new one.
            </p>
          </>
        )}

        {status !== STATUS.LOADING && status !== STATUS.READY && (
          <div className='device-link-actions'>
            <button
              className='device-link-approve'
              onClick={() => { window.location = getBaseURL() }}>
              Return to BaseMap
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  token: state.auth.token
})

const mapDispatchToProps = dispatch => ({
  setLoading: bool => dispatch(setLoading(bool))
})

export default connect(mapStateToProps, mapDispatchToProps)(DeviceLink)
